/**
 * Stores: OlaCore organisations with a website (kuzfit.shop.domain metadata). They are made and read with the API's
 * standard organisation endpoints; only the plugin download (with a new shop key) is KuzFit's own.
 */

const API_ORIGIN = process.env.REACT_APP_API_SERVER_URL || 'https://api.kuzfit.com';
const API_BASE_URL = `${API_ORIGIN}/kuzfit`;
export const DOMAIN_METADATA = 'kuzfit.shop.domain';
const OK = 1; // ENUM_Cmd_Status.ok

const headers = (token) => ({
  'Content-Type': 'application/json',
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
});

const isOk = (resp) => resp && (resp.func_status === OK || resp.func_status === 'ok');

async function call(path, token, init = {}) {
  const response = await fetch(`${API_BASE_URL}/${path}`, { ...init, headers: headers(token) });
  const body = await response.json().catch(() => null);
  if (!response.ok) throw new Error(body?.Error || `${path} failed: ${response.status}`);
  return body;
}

/** The caller's stores from a Read_All_User_Organisations answer: the organisations with a website. */
export function storesFrom(resp) {
  return (resp?.completeOrgList || [])
    .map((org) => ({
      id: org.orgId,
      name: org.orgName,
      domain: (org.metaDataList || []).find((m) => m.metaDataIdentifier === DOMAIN_METADATA)?.metaData,
    }))
    .filter((store) => store.domain);
}

export async function readStores(token) {
  return storesFrom(await call('Read_All_User_Organisations', token, { method: 'POST', body: '{}' }));
}

/** The bare domain of a website, and whether another store has it; throws with the reason when it is not a website. */
export async function checkWebsite(website, token) {
  return call(`stores/website?website=${encodeURIComponent(website)}`, token);
}

/** Makes the store (an organisation with the caller as its admin) and gives its id. */
export async function createStore({ name, domain }, token) {
  const resp = await call('Create_Organisation', token, {
    method: 'POST',
    body: JSON.stringify({
      orgName: name,
      businessType: 1,
      monetaryCurrency: 840,
      latitude: 0,
      longitude: 0,
      applicationGUID: null,
      orgIdentifier: domain,
      isClaimed: 2,
      subscriptionDefinitionId: 0,
      metadataOptionList: [{ metaDataIdentifier: DOMAIN_METADATA, metaData: domain, metaDataGroupIdentifier: 'kuzfit.shop', type: 4 }],
    }),
  });
  if (!isOk(resp) || !(resp.NewRecordID > 0)) throw new Error('Could not add the store');
  return resp.NewRecordID;
}

/** The store's plugin with a new shop key built in (the key in any plugin installed before stops working). */
export async function downloadPlugin(storeId, token) {
  const resp = await call(`stores/${storeId}/plugin`, token, { method: 'POST' });
  const bytes = Uint8Array.from(atob(resp.pluginZip), (c) => c.charCodeAt(0));
  return { ...resp, zip: new Blob([bytes], { type: 'application/zip' }) };
}

/** The calls a store's plugin makes to KuzFit, with its key. */
export function apiCalls(shopKey) {
  const key = encodeURIComponent(shopKey);
  return [
    {
      name: 'Try-on image',
      method: 'GET',
      url: `${API_BASE_URL}/organisationProductView?shopKey=${key}&imageUrl=https%3A%2F%2Fyour-store%2Fproduct.jpg&access_token={shopper's KuzFit token}`,
      returns: 'The product image on the shopper (JPEG). 401 when the shopper is not logged in to KuzFit; 400 when the image is not on the store\'s website.',
    },
    {
      name: 'Shopper preferences',
      method: 'GET',
      url: `${API_BASE_URL}/shopperPreferences?shopKey=${key}&access_token={shopper's KuzFit token}`,
      returns: 'Whether the shopper wants try-ons automatically. 401 once they have logged out.',
    },
  ];
}

export const SWAGGER_URL = `${API_ORIGIN}/swagger`;
